// Runtime configuration — dev/test only.
//
// This module is the SINGLE source of truth for the API base URL
// the SPA talks to. It is hard-coded to the dev/test Worker host
// (`https://id-test.gao.domains`). The build-time env override
// `VITE_GAO_API_BASE` is allowed but the override list is narrowly
// validated so a production host can never enter the runtime
// config — even if an operator's `.env.local` (gitignored) sets
// one by mistake.
//
// A guardrail test (`src/config.test.ts`) asserts:
//   * the default is `https://id-test.gao.domains`,
//   * production hosts (`id.gao.domains`, `api.gao.domains`) are
//     rejected with a clear error message,
//   * any other override must look like a Cloudflare Worker
//     dev URL (`https://*.workers.dev`) or the canonical dev/test
//     host.

const DEFAULT_API_BASE = "https://id-test.gao.domains";

const FORBIDDEN_PRODUCTION_HOSTS: ReadonlySet<string> = new Set([
  "id.gao.domains",
  "api.gao.domains",
]);

/** Returns `true` if `u` is an HTTPS URL pointing at one of the
 *  allowed dev/test targets. */
function isAllowedDevTestBase(u: string): boolean {
  let parsed: URL;
  try {
    parsed = new URL(u);
  } catch {
    return false;
  }
  if (parsed.protocol !== "https:") return false;
  if (FORBIDDEN_PRODUCTION_HOSTS.has(parsed.hostname)) return false;
  if (parsed.hostname === "id-test.gao.domains") return true;
  if (parsed.hostname.endsWith(".workers.dev")) return true;
  // Localhost is allowed for the dev server only; that branch is
  // intentionally narrow.
  if (parsed.hostname === "localhost") return true;
  if (parsed.hostname === "127.0.0.1") return true;
  return false;
}

export class ProductionUrlForbiddenError extends Error {
  constructor(public readonly attempted: string) {
    super(
      `gao-admin-web is dev/test only; refusing to use API base "${attempted}". ` +
        `Allowed: https://id-test.gao.domains, https://*.workers.dev, https://localhost*. ` +
        `Forbidden: id.gao.domains, api.gao.domains.`,
    );
  }
}

export function resolveApiBase(env?: { VITE_GAO_API_BASE?: string }): string {
  const candidate =
    typeof env?.VITE_GAO_API_BASE === "string" && env.VITE_GAO_API_BASE !== ""
      ? env.VITE_GAO_API_BASE
      : DEFAULT_API_BASE;
  if (!isAllowedDevTestBase(candidate)) {
    throw new ProductionUrlForbiddenError(candidate);
  }
  return candidate.replace(/\/+$/, "");
}

export const API_BASE = resolveApiBase(
  // `import.meta.env` is populated by Vite. In a test environment
  // where Vite is not driving the bundle (vitest) the global is
  // still defined by happy-dom + Vite's test config; the fallback
  // covers the no-bundle case.
  typeof import.meta !== "undefined" && import.meta.env
    ? { VITE_GAO_API_BASE: import.meta.env.VITE_GAO_API_BASE as string | undefined }
    : undefined,
);

/** The hard-coded dev/test default — used by guardrail tests +
 *  the README banner. NEVER changes without an explicit PR + audit
 *  trail. */
export const DEVTEST_API_BASE = DEFAULT_API_BASE;

/**
 * Canonical dev/test login audience the SPA sends on every
 * `/v2/admin/owner/auth/challenge` request. The BE stores the
 * audience on the challenge row and re-checks it on verify, so
 * any stable non-empty string the SPA chooses is fine — but it
 * must match exactly between the two requests. We use the SPA's
 * own custom domain so the audience is traceable from the BE
 * audit row.
 */
export const ADMIN_LOGIN_AUDIENCE = "https://admin-test.gao.domains";

/**
 * The SIWE `domain` and `uri` this console signs with.
 *
 * They must match `gao-id-worker`'s SIWE_DOMAIN and ALLOWED_ORIGINS exactly, and
 * they are constants rather than `window.location` readings on purpose: a
 * framed, proxied or wrapped page must not be able to produce a message for an
 * origin this deployment never intended to authorize.
 */
export const ADMIN_SIWE_DOMAIN = "admin-test.gao.domains";
export const ADMIN_SIWE_URI = "https://admin-test.gao.domains";

// ─── Two different chains, and conflating them was a defect ────────────────
//
// The console used to hold ONE chain constant, 84532, and use it for two
// unrelated questions: "where does GaoSafe authority live" and "what chain must
// the wallet be on to log in". Those have different answers, and answering the
// second with the first made the login screen tell an operator on Base mainnet
// that their correct configuration was a "Wrong chain" error — then offer to
// "fix" it by switching them to the authority chain.
//
// Under the ratified chain law the wallet ADDRESS is the Admin login identity.
// The connected chain is protocol metadata that binds the SIWE nonce, nothing
// more. The GaoSafe authority chain is decided server-side and is never a
// constraint on the wallet.

/**
 * The GaoSafe AUTHORITY chain — where the server reads live vault membership.
 *
 * Mirrors `gao-id-worker`'s `GAOSAFE_AUTHORITY_CHAIN_ID`. It is presentation-only
 * here: the server never trusts a chain the browser names, and this value must
 * NEVER gate, compare against, or switch the connected wallet. It exists so the
 * post-login authority panel can say where authority is read from.
 */
export const GAOSAFE_AUTHORITY_CHAIN_ID = 84532;
export const GAOSAFE_AUTHORITY_CHAIN_NAME = "Base Sepolia";

/**
 * The canonical Gao SIWE login-chain allowlist.
 *
 * Mirrors `gao-id-worker`'s `CHAIN_ID_ALLOWLIST`, which `verifyAndConsume`
 * enforces server-side — this copy decides only what the UI PRESENTS as
 * acceptable, and a chain the server would refuse must not look acceptable here.
 * If the backend list changes, this changes with it.
 */
export const SIWE_CHAIN_ALLOWLIST: readonly number[] = [
  1, 10, 56, 97, 137, 8453, 42161, 84532, 421614, 11155111, 11155420,
];

/** Human names for the chains an operator is plausibly on. Presentation only. */
const CHAIN_NAMES: Readonly<Record<number, string>> = {
  1: "Ethereum",
  10: "OP Mainnet",
  56: "BNB Chain",
  97: "BNB Testnet",
  137: "Polygon",
  8453: "Base",
  42161: "Arbitrum One",
  84532: "Base Sepolia",
  421614: "Arbitrum Sepolia",
  11155111: "Sepolia",
  11155420: "OP Sepolia",
};

/**
 * May an operator log in from this connected chain?
 *
 * The allowlist is the ONLY rule. There is deliberately no comparison against
 * `GAOSAFE_AUTHORITY_CHAIN_ID` — being on the authority chain is neither
 * required nor special.
 */
export function isAllowedLoginChain(id: number | null | undefined): id is number {
  return typeof id === "number" && SIWE_CHAIN_ALLOWLIST.includes(id);
}

/** A chain's display label. Falls back to the bare id for an unknown chain. */
export function chainLabel(id: number | null | undefined): string {
  if (typeof id !== "number") return "Unknown";
  return CHAIN_NAMES[id] ?? `Chain ${id}`;
}

/**
 * RETAINED FOR THE SETTLEMENT SURFACE ONLY — not for login.
 *
 * `AffiliateWithdrawalDetail` uses these to name the chain a withdrawal settles
 * on, which genuinely is 84532. Nothing on the login path may read them; the
 * login-chain rule is `isAllowedLoginChain`.
 */
export const REQUIRED_CHAIN_ID = GAOSAFE_AUTHORITY_CHAIN_ID;
export const REQUIRED_CHAIN_NAME = GAOSAFE_AUTHORITY_CHAIN_NAME;

/**
 * Default `ownerSubject` for the dev/test owner login. A PREFILL, never a
 * constraint: it saves retyping when the AuthRequired guard bounces an operator
 * back here, and the field stays editable.
 *
 * It names no wallet, deliberately. Any CURRENT GaoSafe owner may authenticate —
 * there are several legitimate owner wallets — and which one is signing is
 * settled by the signature the server recovers and the live `isOwner` read it
 * performs, never by anything this bundle believes. A console that pinned a
 * single address here would refuse a legitimate owner for no reason it could
 * justify.
 */
export const DEFAULT_OWNER_SUBJECT = "op-cb-2026";

/**
 * Optional WalletConnect / Reown project ID. The Admin Web
 * deliberately does NOT hardcode a fallback (per the operator's
 * documented rule "never hardcode fallbacks" — the canonical
 * project ID lives in 1Password and is supplied at build time).
 * When this env var is unset, the SPA renders the WalletConnect
 * option as BLOCKED with a clear "operator must set
 * VITE_WALLETCONNECT_PROJECT_ID at build time" notice.
 */
export const WALLETCONNECT_PROJECT_ID: string | null =
  typeof import.meta !== "undefined" &&
  typeof import.meta.env?.VITE_WALLETCONNECT_PROJECT_ID === "string" &&
  import.meta.env.VITE_WALLETCONNECT_PROJECT_ID !== ""
    ? (import.meta.env.VITE_WALLETCONNECT_PROJECT_ID as string)
    : null;
